import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { coursesApi, liveCoursesApi } from "@/api";
import type {
  CourseFilter,
  CourseStatus,
  CourseType,
  CreateCourseData,
  UpdateAdmissionData,
  UpdateCourseData,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

import { useCourseListQuery } from "./use-course-list-query";
import type { CourseScope } from "./use-course-list-query";

export * from "./use-course-list-query";

/**
 * Kurslar ro'yxati, rolga qarab:
 *  - "mine" → o'qituvchining o'z kurslari (`/courses/my`)
 *  - "all"  → to'liq katalog (`/courses`); admin hamma holatni oladi.
 */
export const useCourseList = (filter: CourseFilter, scope: CourseScope, enabled = true) =>
  useCourseListQuery("recorded", filter, scope, enabled);

/** Katalog plitalarida ko'rsatiladigan holatlar. */
export const CATALOG_STATUSES: readonly CourseStatus[] = [
  "DRAFT",
  "PENDING_REVIEW",
  "PUBLISHED",
  "REJECTED",
  "ARCHIVED",
];

/**
 * Holat bo'yicha kurslar soni.
 *
 * Katalog uchun yig'ma endpoint YO'Q (`/courses/moderation/stats` faqat
 * uchta moderatsiya holatini biladi va jonli kurslarni ajratmaydi), shuning
 * uchun har bir holat uchun `limit: 1` bilan bitta so'rov ketadi va
 * `meta.total` o'qiladi — son HAQIQIY, soxta nol emas.
 */
export const useCourseStatusCounts = (type: CourseType) => {
  const source = type === "LIVE" ? liveCoursesApi : coursesApi;
  const key = type === "LIVE" ? QUERY_KEYS.GET_LIVE_COURSES : QUERY_KEYS.GET_COURSES;

  const queries = useQueries({
    queries: CATALOG_STATUSES.map(status => ({
      queryKey: [key, "count", status],
      // `type` shart: `/courses` jonli kurslarni ham qaytaradi va ular
      // "yozib olingan" ustunida ham sanalardi.
      queryFn: () => source.getAll({ page: 1, limit: 1, status, type }),
      select: (res: Awaited<ReturnType<typeof source.getAll>>) => res.meta.total,
    })),
  });

  const counts = Object.fromEntries(
    CATALOG_STATUSES.map((status, index) => [status, queries[index]?.data]),
  ) as Record<CourseStatus, number | undefined>;

  return {
    counts,
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

export const useCourse = (id: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE, id],
    queryFn: () => coursesApi.getById(id as number),
    enabled: !!id,
    select: res => res.data,
  });
};

/**
 * Jadval sarlavhasidagi "Kurs" filtri uchun variantlar.
 *
 * Hook emas: `meta.filterOptionsAsync` shaklidagi `{ queryKey, queryFn }`
 * qaytaradi va so'rov faqat filtr popoveri ochilganda ketadi — sahifa butun
 * katalogni oldindan yuklamaydi. `/courses` jonli kurslarni ham qaytaradi,
 * shuning uchun bitta ro'yxat ikkala turga yetadi.
 */
export const courseFilterOptions = (input: string) => ({
  queryKey: [QUERY_KEYS.GET_COURSES, "filter-options", input],
  queryFn: async () => {
    const res = await coursesApi.getAll({ page: 1, limit: 20, search: input.trim() || undefined });
    return res.data.map(course => ({ label: course.title, value: course.id }));
  },
});

/**
 * URL'dan kelgan kurs id'sining nomi (`meta.filterOptionLabel`).
 *
 * Kalit `useCourse` nikidan farq qiladi: u yerda keshda butun javob yotadi,
 * bu yerda esa faqat sarlavha — bitta kalit ostida ikki xil shakl bo'lsa,
 * biri ikkinchisini buzardi.
 */
export const courseFilterLabel = (value: string) => ({
  queryKey: [QUERY_KEYS.GET_COURSE, "filter-label", value],
  queryFn: async () => {
    const id = Number(value);
    if (!Number.isInteger(id) || id <= 0) return null;
    const res = await coursesApi.getById(id);
    return res.data.title;
  },
});

export const useCreateCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCourseData) => coursesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCourseData }) =>
      coursesApi.update(id, data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, variables.id] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.remove(id),
    // O'chirish arxivlash/tiklashdan farq qilmaydi: kurs katalogdan
    // chiqadi va savatga tushadi, ya'ni ikkala ro'yxat ham eskiradi.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_DELETED_COURSES] });
      toast.success(t("COMMON.DELETED"));
    },
  });
};

export const useSubmitCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      toast.success(t("COURSES.SUBMITTED"));
    },
  });
};

/**
 * Sehrgarning REVIEW qadami uchun nashrga tayyorlik ro'yxati. Har safar
 * yangidan olinadi (`staleTime` 0), chunki u oldingi qadamlarda o'quv rejasiga
 * kiritilgan o'zgarishlarni aks ettiradi.
 */
export const useCourseReadiness = (id: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_READINESS, id],
    queryFn: () => coursesApi.getReadiness(id as number),
    enabled: !!id,
    staleTime: 0,
    select: res => res.data,
  });
};

// ─── Moderatsiya (moderator / admin) ─────────────────────────────────────────

/** Moderatsiya paneli uchun umumiy sonlar. */
export const useModerationStats = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MODERATION_STATS],
    queryFn: () => coursesApi.getModerationStats(),
    select: res => res.data,
  });
};

/** Moderatsiya ro'yxati — kutayotgan navbat (standart) yoki nashr/rad tarixi. */
export const useModerationList = (filter: CourseFilter) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MODERATION_LIST, filter],
    queryFn: () => coursesApi.getModerationList(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });
};

/**
 * Invalidates every moderation-affected query after a decision.
 *
 * Moderatsiya ekrani jonli kurslarni ham SHU mutatsiyalar orqali o'tkazadi
 * (`moderation-review-view.tsx`), shuning uchun jonli kurs kalitlari ham
 * bu yerda: ularsiz tasdiqlangan jonli kurs sahifa qayta yuklanmaguncha
 * "kutilmoqda" bo'lib turardi.
 */
const invalidateModeration = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MODERATION_STATS] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MODERATION_LIST] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_COURSES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_LIVE_COURSES] });
};

export const useApproveCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.approve(id),
    onSuccess: (_res, id) => {
      invalidateModeration(queryClient);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, id] });
      toast.success(t("MODERATION.APPROVED_TOAST"));
    },
  });
};

export const useRejectCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => coursesApi.reject(id, reason),
    onSuccess: (_res, { id }) => {
      invalidateModeration(queryClient);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, id] });
      toast.success(t("MODERATION.REJECTED_TOAST"));
    },
  });
};

// ─── Tanlanganlar / arxivlash ────────────────────────────────────────────────

/** Kursni bosh sahifa qatoriga qadaydi yoki undan oladi (moderator / admin). */
export const useSetCourseFeatured = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, featured }: { id: number; featured: boolean }) =>
      coursesApi.setFeatured(id, featured),
    onSuccess: (_res, { id, featured }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, id] });
      toast.success(t(featured ? "COURSES.FEATURED_TOAST" : "COURSES.UNFEATURED_TOAST"));
    },
  });
};

/**
 * O'chirish emas, arxivlash.
 *
 * O'quvchisi bor kursni o'chirishni backend rad etadi — bu uning taklif
 * qiladigan yo'li: kurs katalogdan chiqadi, pul to'lagan har bir odam esa
 * kirish huquqini saqlab qoladi.
 */
export const useArchiveCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.archive(id),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, id] });
      toast.success(t("COURSES.ARCHIVED_TOAST"));
    },
  });
};

// ─── Qabul ────────────────────────────────────────────────────────────────────

/**
 * Qabulni to'xtatish/davom ettirish va oxirgi kun. Javobdagi kurs darhol
 * keshga yoziladi — tugma holati serverning qayta hisoblagan `admission`i
 * bilan bir xil bo'ladi.
 */
export const useUpdateAdmission = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAdmissionData) => coursesApi.updateAdmission(courseId, data),
    onSuccess: res => {
      queryClient.setQueryData([QUERY_KEYS.GET_COURSE, courseId], res);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LIVE_COURSES] });
      toast.success(t("ADMISSION.SAVED_TOAST"));
    },
  });
};

// ─── Nashrdan keyingi tahrirlar izi ──────────────────────────────────────────

/** Kurs nashr etilgandan keyin unga kiritilgan tahrirlar. */
export const useCourseHistory = (id: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_HISTORY, id],
    queryFn: () => coursesApi.getHistory(id as number),
    enabled: !!id,
    select: res => res.data,
  });

// ─── Chiqindi qutisi (admin) ─────────────────────────────────────────────────

/** Yumshoq o'chirilgan kurslar — o'chirish endi qatorni yo'q qilmaydi. */
/**
 * Savatdagi kurslar — faqat admin uchun (`@AdminOnly`). Moderatorda so'rov
 * umuman yuborilmaydi: ilgari Kurslar sahifasi har ochilganda moderatorga
 * sababsiz 403 xabari chiqarardi.
 */
export const useDeletedCourses = (filter: CourseFilter, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_DELETED_COURSES, filter],
    queryFn: () => coursesApi.getDeleted(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
    enabled,
  });

/** Yumshoq o'chirilgan kursni qaytaradi; kerak bo'lsa backend slugni yangilaydi. */
export const useRestoreCourse = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_DELETED_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_COURSES] });
      toast.success(t("COURSES.RESTORED_TOAST"));
    },
  });
};
